import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import NumberFlow from '@number-flow/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { Price, PriceAPI, Stats, StatsAPI } from 'types/api/websiteApi/price';

import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';

import IconSvg from '../IconSvg';
import { LatestPriceContent } from './LatestPriceContent';

export type TopLevelStatsProps = {
  showBlockData?: boolean | null;
  latestBlock?: number;
};

const taoDivider = 1_000_000_000;

const calculateMarketCap = (priceData: Price | undefined, statsData: Stats | undefined) => {
  if (!priceData || !statsData) {
    return 0;
  }

  const marketCap = Number(priceData.price) * Number(statsData.issued) / taoDivider;
  return marketCap;
};

export const useMarketCap = (priceData: Price | undefined, statsData: Stats | undefined) => {
  return useMemo(
    () => calculateMarketCap(priceData, statsData),
    [ priceData, statsData ],
  );
};

const useWebsiteStatsQuery = () => {
  const query = useQuery({
    queryKey: [ 'website-api', 'stats' ],
    queryFn: async() => {
      const response = await fetch('/api/website-api-proxy/stats/latest/v1');
      const responseJson = await response.json();
      return responseJson as StatsAPI;
    },
    staleTime: 12000,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  return query;
};

const usePriceQuery = () => {
  const query = useQuery({
    queryKey: [ 'website-api', 'price' ],
    queryFn: async() => {
      const response = await fetch('/api/website-api-proxy/price/latest/v1?asset=tao');
      const responseJson = await response.json();
      return responseJson as PriceAPI;
    },
    staleTime: 12000,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  return query;
};

export const TopLevelStats = ({
  latestBlock,
  showBlockData = false,
}: TopLevelStatsProps) => {
  const priceQuery = usePriceQuery();
  const statsQuery = useWebsiteStatsQuery();

  const marketCap = useMarketCap(priceQuery.data?.data[0], statsQuery.data?.data[0]);

  const bgColor = useColorModeValue('blackAlpha.900', 'black');
  const textColor = useColorModeValue('#909090', '#909090');
  const valueColor = useColorModeValue('white', 'white');

  if (!priceQuery.isSuccess || !statsQuery.isSuccess) {
    return null;
  }

  const priceData = priceQuery.data.data[0];

  return (
    <Box position="sticky" top={ 0 } zIndex="sticky" borderBottom="1px solid" borderColor="#363636">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        maxW={ `${ CONTENT_MAX_WIDTH }px` }
        m="0 auto"
      >
        <Flex
          flexDir="column"
          bgColor={ bgColor }
          backdropFilter="blur(12px)"
          px={{ base: 2, sm: 5 }}
          py={ 2 }
          flex="1"
        >
          <Flex
            alignItems="center"
            justifyContent="flex-start"
            gap={{ base: 2, sm: 8 }}
            flexWrap={{ base: 'wrap', md: 'nowrap' }}
          >
            <LatestPriceContent data={ priceData }/>
            { priceData && (
              <>
                <Flex alignItems="center" gap={{ base: 1, sm: 3 }}>
                  <Text fontSize="sm" color={ textColor }>Market Cap</Text>
                  <Text fontSize="sm" color={ valueColor } fontWeight="500">
                    $
                    <NumberFlow
                      format={{
                        notation: 'compact',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }}
                      value={ marketCap }
                    />
                  </Text>
                </Flex>
                <Flex alignItems="center" gap={ 3 } display={{ base: 'none', lg: 'flex' }}>
                  <Text fontSize="sm" color={ textColor }>24hr Volume</Text>
                  <Text fontSize="sm" color={ valueColor } fontWeight="500">
                    $
                    <NumberFlow
                      format={{
                        notation: 'compact',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }}
                      value={ Number(priceData.volume_24h) }
                    />
                  </Text>
                </Flex>
              </>
            ) }

            { showBlockData && (
              <Flex
                alignItems="center"
                gap={ 2 }
                color={ valueColor }
                display={{ base: 'none', sm: 'flex' }}
                fontSize="sm"
              >
                <IconSvg name="block_slim" boxSize={ 5 } p="1px" ml={ 2 } flexShrink={ 0 }/>
                <NumberFlow format={{ useGrouping: false }} value={ latestBlock ?? 0 }/>
              </Flex>
            ) }
          </Flex>
        </Flex>
        <Box py={ 2 } px={{ base: 2, md: 7 }} flexShrink={ 0 }>
          <Box
            textAlign="right"
            as="a"
            href="https://taostats.io"
            display="inline-flex"
            overflow="hidden"
            aria-label="Link to taostats.io"
            _hover={{ textDecoration: 'underline' }}
          >
            <Text color="white" fontSize="12px" fontWeight="bold">&laquo; Back to Taostats</Text>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};
